import { h, Component } from 'preact';
import Store from 'store/store';
import { arrayUnique, joinClasses, sortObjectByOrder } from 'utils/utils';
import { TakeAgain } from 'components/bolt/icons/quizPhotoboothIcons';
import { navigateInternally } from 'utils/bolt';
import HTML from 'components/core/html/html';
import Socials from 'components/bolt/socials/socials';
import s from 'components/bolt/quizPhotobooth/shareable.sss';

const loadImage = (src) => new Promise((resolve, reject) => {
	const img = new Image();
	img.crossOrigin = 'anonymous';
	img.onload = () => resolve(img);
	img.onerror = reject;
	img.src = src;
});

export default class Shareable extends Component {
	constructor(props) {
		super(props);
		this.onTouchStart = this.onTouchStart.bind(this);
		this.onTouchMove = this.onTouchMove.bind(this);
		this.onTouchEnd = this.onTouchEnd.bind(this);
		this.shareOrDownload = this.shareOrDownload.bind(this);
		this.getStylingVariable = this.getStylingVariable.bind(this);
		this.resetShareable = this.resetShareable.bind(this);
		this.handleLinkClick = this.handleLinkClick.bind(this);
		this.generateText = this.generateText.bind(this);
		this.onPrevFrameClick = this.onPrevFrameClick.bind(this);
		this.onNextFrameClick = this.onNextFrameClick.bind(this);
		this.confirmFrameSelection = this.confirmFrameSelection.bind(this);
		this.restart = this.restart.bind(this);
		this.$videoFeed = null;
		this.$videoStories = null;
		//const storiesFirst = props.shareable?.stories?.order < props.shareable?.feed?.order;

		this.allowChoosingFrame = this.props.shareable?.allowChoosingFrame;
		this.sortedResultData = sortObjectByOrder(this.props.resultData);

		this.state = {
			canShare: false,
			currentCard: 'first',
			cardBasesDrawn: [],
			photoAdded: [],
			loadedFonts: [],
			frameSelectedIndex: 0,
			frameConfirmed: false,
			frameCardBasesDrawn: [],
			framePhotosAdded: [],
		};
	}

	get appData() {
		return this.props.appData || Store.get().appData;
	}
	get fontStyles() {
		return this.appData?.styling?.fontVariables || {};
	}
	get copy() {
		return this.appData?.copy?.baseCopy || {};
	}

	async componentDidMount() {
		Store.on('QUIZPHOTOBOOTH_SHAREABLE_RESET', this.resetShareable);
		// if (this.props.shareable.shareableVideo) {
		// 	return;
		// }
		this.feedContext = this.$feedCanvas.getContext('2d');
		this.storiesContext = this.$storiesCanvas.getContext('2d');
		try {
			this.cutOutPhoto();
			await this.loadFonts();
		} catch (error) {
			console.error(error);
		}
		if (this.allowChoosingFrame) {
			this.prepareFrameChoices();
			this.setFrameCarouselTransform();
		} else {
			this.prepareShareables();
		}
	}

	componentDidUpdate(prevProps) {
		if (this.allowChoosingFrame && prevProps.client?.dimensions?.width !== this.props.client?.dimensions?.width) {
			this.setFrameCarouselTransform();
		}
	}

	componentWillUnmount() {
		Store.off('QUIZPHOTOBOOTH_SHAREABLE_RESET', this.resetShareable);
	}

	restart() {
		this.setState({ frameConfirmed: false });
		this.props.backToIntro();
	}

	setFrameCarouselTransform(frameSelectedIndex) {
		frameSelectedIndex = frameSelectedIndex || this.state.frameSelectedIndex;
		if (this.$frameCarousel.clientWidth < this.$frameOptions.clientWidth) {
			const frameCardWidth = this[`chooseFrame${this.sortedResultData[0].id}`].offsetWidth || 0;
			const frameCarouselBaseTransform = (window.innerWidth - (frameCardWidth + 32)) / 2;
			const finalTransform = frameCarouselBaseTransform - frameSelectedIndex * (frameCardWidth + 32);
			this.setState({
				noFrameCarouselScroll: false,
				frameCarouselTransform: finalTransform
			});
		} else {
			this.setState({
				noFrameCarouselScroll: true,
				frameCarouselTransform: 0
			});
		}
	}

	// If fonts are provided in $mediaroot/fonts, load them on component mount
	async loadFonts() {
		const shareable = this.props.shareable;
		const feedTextFields = shareable.feed.textfields;
		const feedTextFieldArray = Object.values(feedTextFields);
		const storiesTextFields = shareable.stories.textfields;
		const storiesTextFieldArray = Object.values(storiesTextFields);
		const allTextFields = feedTextFieldArray.concat(storiesTextFieldArray);
		const uniqueFonts = arrayUnique(allTextFields.map(textField => textField.font));

		await Promise.all(uniqueFonts.map(async (fontName) => {
			if (!fontName.includes('.')) return;
			try {
				const fontUrl = `${this.appData?.urls.roots.media}/fonts/${fontName}`;
				const newFont = new FontFace(fontName.split('.')[0], `url("${fontUrl}")`);
				await newFont.load();
				document.fonts.add(newFont);
				// let state know that font has already been loaded
				this.state.loadedFonts.push(fontName);
			} catch (error) {
				console.error(`Font loading error: ${error}`);
			}
		}));
	}

	async prepareShareables(opts) {
		try {
			// TODO: break out text placement from within drawBases
			// because currently the photo is placed after text,
			// which means it will cover text if it's in the same spot
			// (see wicked override)
			await this.drawBases({ useFrameChoice: opts?.useFrameChoice });
			this.addPhotos();
			this.setState({
				storiesImageSrc: this.$storiesCanvas.toDataURL('image/png'),
				feedImageSrc: this.$feedCanvas.toDataURL('image/png')
			});
		} catch (error) {
			console.error(error);
		}
	}

	async prepareFrameChoices() {
		const { feed } = this.props.shareable;
		try {
			for (const result of this.sortedResultData) {
				const cardCanvas = this[`chooseFrame${result.id}`];
				const cardContext = cardCanvas.getContext('2d');
				await this.drawCard({
					type: result.id,
					baseImgSrc: result.shareableImageFeed,
					cardCanvas,
					cardContext,
				});
				this.addPhotoToCard({
					cardSize: `${result.id}-feed`,
					cardCanvas,
					cardContext,
					xPos: feed.photo?.xPos,
					yPos: feed.photo?.yPos,
					photoRadius: feed.photo?.photoRadius
				});
			}
		} catch (error) {
			console.error(error);
		}
	}

	async drawBases(opts) {
		return Promise.all([
			this.drawCard({
				type: 'feed',
				baseImgKey: 'shareableImageFeed',
				cardCanvas: this.$feedCanvas,
				cardContext: this.feedContext,
				baseImgSrc: opts?.useFrameChoice && this.sortedResultData[this.state.frameSelectedIndex]?.shareableImageFeed
			}),
			this.drawCard({
				type: 'stories',
				baseImgKey: 'shareableImageStories',
				cardCanvas: this.$storiesCanvas,
				cardContext: this.storiesContext,
				baseImgSrc: opts?.useFrameChoice && this.sortedResultData[this.state.frameSelectedIndex]?.shareableImageStories
			})
		]);
	}

	addPhotos() {
		const { feed, stories } = this.props.shareable;
		this.addPhotoToCard({
			type: 'feed',
			cardCanvas: this.$feedCanvas,
			cardContext: this.feedContext,
			cardSize: 'feed',
			xPos: feed.photo?.xPos,
			yPos: feed.photo?.yPos,
			photoRadius: feed.photo?.photoRadius
		});
		this.addPhotoToCard({
			type: 'stories',
			cardCanvas: this.$storiesCanvas,
			cardContext: this.storiesContext,
			cardSize: 'stories',
			xPos: stories.photo?.xPos,
			yPos: stories.photo?.yPos,
			photoRadius: stories.photo?.photoRadius
		});
	}

	getStylingVariable(string) {
		if (string.startsWith('$')) {
			return this.appData?.styling?.variables?.[string.slice(1)];
		}
		return string;
	}

	onTouchStart(e) {
		if (e.touches.length === 1) {
			this.lastClientX = e.touches[0].clientX;
		}
	}

	onTouchMove(e) {
		if (e.touches.length === 1) {
			const clientX = e.touches[0].clientX;
			const diffX = clientX - this.lastClientX;
			this.diffX = diffX;
		}
	}

	onTouchEnd() {
		if (Math.abs(this.diffX) > 15) {
			const card = this.diffX > 0 ? 'first' : 'second';
			this.swapToCard(card);
		}
		this.diffX = null;
		this.lastClientX = null;
	}

	resetShareable() {
		this.setState({
			currentCard: 'first',
			cardBasesDrawn: [],
			photoAdded: []
		});
	}

	swapToCard(card) {
		if (card === this.state.currentCard) return;
		this.setState({
			currentCard: card
		});
		Store.emit('TRACK_QUIZPHOTOBOOTH_CAROUSEL_SWIPE');
		if (!this.props.shareable.shareableVideo) return;
		if (card === 'first') {
			this.$videoStories.pause();
			this.$videoFeed.play();
		}
		if (card === 'second') {
			this.$videoFeed.pause();
			this.$videoStories.play();
		}
	}

	async generateText(textSettings, cardContext, cardCanvas, quizResultData) {
		let content;
		if (textSettings.setText) {
			content = textSettings.setText;
		} else if (textSettings.contentKey === 'result') {
			content = quizResultData?.copy || 'Quiz Result';
		} else if (this.props.inputs[textSettings.contentKey]) {
			content = this.props.inputs[textSettings.contentKey];
		} else {
			return;
		}

		if (textSettings.casing === 'lowercase') {
			content = content.toLowerCase();
		} else if (textSettings.casing === 'uppercase') {
			content = content.toUpperCase();
		} else if (textSettings.casing === 'capitalize') {
			content = content.toLowerCase();
			// Based (loosely) on CSS text-transform: capitalize
			// Proper word-boundary rules are a bit too complex to implement (https://www.unicode.org/reports/tr29/#Word_Boundary_Rules) and won't matter much hopefully
			content = content.replace(/((?:^|\p{Separator}|-).*?)(\p{Letter}|\p{Number})/gu, (_, punctuation, initial) => `${punctuation}${initial.toUpperCase()}`);
		}

		cardContext.textAlign = textSettings.textAlign || 'center';
		cardContext.fillStyle = this.getStylingVariable(textSettings.color) || '#DEC181';

		const fontData = textSettings.font;
		const fontName = fontData.split('.')[0];

		let font;
		// If font is provided through external stylesheet (Start => Html Document => Style Links):
		if (!this.state.loadedFonts.includes(fontName)) {
			try {
				// Ensure the font is loaded before using it
				await document.fonts.load(`${textSettings.fontWeight || 400} ${textSettings.fontSize || 18}px ${textSettings.font}`);
			} catch (error) {
				console.error(`Font loading error: ${error}`);
				return;
			}
		}
		font = `${textSettings.fontWeight || 400} ${textSettings.fontSize || 18}px ${fontName}`;
		cardContext.font = font;

		const xPos = textSettings.centerX ? cardCanvas.width / 2 : textSettings.xPos;
		let yPos = textSettings.yPos;
		let maxWidth = textSettings.maxWidth || cardCanvas.width;

		if (cardContext.textAlign === 'left') {
			maxWidth = maxWidth - xPos * 2;
		}
		let width = cardContext.measureText(content).width;
		let newFontSize = textSettings.fontSize || 18;

		while (width > maxWidth && newFontSize > 0) {
			newFontSize = newFontSize - 1;
			yPos = yPos - 0.5;
			cardContext.font = `${textSettings.fontWeight || 400} ${newFontSize}px ${textSettings.font}`;
			width = cardContext.measureText(content).width;
		}

		cardContext.fillText(content, xPos, yPos, maxWidth);
	}

	async drawCard({ type, baseImgKey, cardCanvas, cardContext, baseImgSrc }) {
		if (this.state.cardBasesDrawn.includes(type)) {
			return;
		}
		this.setState({ cardBasesDrawn: [...this.state.cardBasesDrawn, type] });

		const { quizResult, resultData, shareable } = this.props;
		const { frameSelectedIndex } = this.state;
		const shareableData = shareable[type] || shareable.feed;

		// selects the winning quiz result, or the first available result if no quiz result
		let quizResultData = quizResult ? resultData[quizResult] : this.sortedResultData[0];

		if (this.allowChoosingFrame) {
			quizResultData = this.sortedResultData[frameSelectedIndex];
		}

		// baseImgKey can either be 'frameFeed' or 'frameStories'
		let src = quizResultData?.[baseImgKey];
		if (quizResultData?.shareableBackgroundColor) {
			const sharedImageRoot = this.appData.urls?.roots?.sharedImages;
			const dummyImageSrc = `${sharedImageRoot}/quizPhotobooth/dummy_${type}.png`;
			src = dummyImageSrc;
		}

		if (baseImgSrc) {
			src = baseImgSrc;
		}
		const baseImage = await loadImage(src);

		cardCanvas.width = baseImage.width || 400;
		cardCanvas.height = baseImage.height || (type === 'feed' ? 500 : 712);
		cardContext.drawImage(baseImage, 0, 0, cardCanvas.width, cardCanvas.height);

		if (quizResultData?.shareableBackgroundColor) {
			cardContext.fillStyle = quizResultData.shareableBackgroundColor;
			cardContext.fillRect(0, 0, cardCanvas.width, cardCanvas.height);
		}

		cardContext.textBaseline = 'bottom';

		Object.keys(shareableData?.textfields || {}).forEach(textfieldName => {
			const textfield = shareableData?.textfields[textfieldName];
			this.generateText(textfield, cardContext, cardCanvas, quizResultData);
		});
	}

	cutOutPhoto() {
		this.photoCanvas = Store.get().quizPhotobooth.photo;
		const photoCanvasContext = this.photoCanvas.getContext('2d');
		photoCanvasContext.globalCompositeOperation = 'destination-in';
		photoCanvasContext.fillStyle = '#000';
		photoCanvasContext.beginPath();
		// photoCanvasContext.arc(
		// 	this.photoCanvas.height / 2,
		// 	this.photoCanvas.height / 2,
		// 	this.photoCanvas.height / 2,
		// 	0,
		// 	2 * Math.PI
		// );
		const photoBorderRadius = this.props.shareable?.photoBorderRadius || 16;
		photoCanvasContext.roundRect(
			0, 0, this.photoCanvas.width, this.photoCanvas.height, photoBorderRadius
		);
		photoCanvasContext.fill();
		photoCanvasContext.globalCompositeOperation = 'source-over';
	}

	addPhotoToCard({ cardCanvas, cardContext, cardSize, xPos, yPos, photoRadius }) {
		if (this.state.photoAdded.includes(cardSize)) {
			return;
		}
		this.setState({ photoAdded: [...this.state.photoAdded, cardSize] });
		cardContext.drawImage(
			this.photoCanvas,
			xPos,
			yPos,
			photoRadius * 2,
			photoRadius * 2
		);
		this.prepareShareAndDownloadData(cardCanvas, cardSize);
	}

	handleLinkClick() {
		const path = this.props.shareable.link.path;
		if (path.startsWith('http') || path.startsWith('mailto:')) {
			let link = document.createElement('a');
			link.href = path;
			link.target = '_blank';
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else {
			navigateInternally(path);
		}
	}

	prepareShareAndDownloadData(cardCanvas, size) {
		const filenames = {
			['feed']: 'shareable-feed',
			['stories']: 'shareable-story'

		};
		const filename = (this.props.shareable?.[size]?.filename || `${filenames[size]}`) + '.png';
		const dataURL = cardCanvas.toDataURL('image/png');

		let shareData;
		let canShare;
		cardCanvas.toBlob(blob => {
			const files = [
				new File([blob], filename, { type: 'image/png' })
			];
			shareData = {
				files
			};
			canShare = navigator.canShare && navigator.canShare(shareData);
			this[`${size}Data`] = {
				canShare,
				shareData,
				dataURL,
				filename
			};
		});
	}

	async shareOrDownload() {
		const { currentCard, frameSelectedIndex } = this.state;
		const { isDesktopDevice, shareable } = this.props;
		const storiesFirst = shareable?.storiesFirst || false;
		let cardData;
		if (currentCard === 'first') {
			cardData = storiesFirst ? this.storiesData : this.feedData;
		} else {
			cardData = storiesFirst ? this.feedData : this.storiesData;
		}
		const { canShare, shareData, dataURL, filename } = cardData;
		const useDownload = isDesktopDevice || !canShare;

		const trackingType = useDownload ? 'save' : 'share';
		let trackingFormatLabel;
		if (storiesFirst) {
			trackingFormatLabel = currentCard === 'first' ? 'stories' : 'feed';
		} else {
			trackingFormatLabel = currentCard === 'first' ? 'feed' : 'stories';
		}
		if (this.allowChoosingFrame) {
			trackingFormatLabel += `-${this.sortedResultData[frameSelectedIndex].id}`;
		}
		Store.emit('TRACK_QUIZPHOTOBOOTH_SHARE_CARD_CLICK', trackingType, trackingFormatLabel);

		if (useDownload) {
			const link = document.createElement('a');
			link.download = filename;
			link.href = dataURL;
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else {
			navigator.share(shareData);
		}
	}

	selectFrame(index) {
		this.setState({
			frameSelectedIndex: index
		});
	}

	onPrevFrameClick() {
		const newFrameSelectedIndex = this.state.frameSelectedIndex - 1;
		this.selectFrame(newFrameSelectedIndex);
		this.setFrameCarouselTransform(newFrameSelectedIndex);
	}

	onNextFrameClick() {
		const newFrameSelectedIndex = this.state.frameSelectedIndex + 1;
		this.selectFrame(newFrameSelectedIndex);
		this.setFrameCarouselTransform(newFrameSelectedIndex);
	}

	async confirmFrameSelection() {
		Store.emit('TRACK_QUIZPHOTOBOOTH_CHOOSE_FRAME', this.sortedResultData[this.state.frameSelectedIndex].id);
		await this.prepareShareables({ useFrameChoice: true });
		this.setState({
			frameConfirmed: true
		});
		Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', 'shareable');
	}

	renderBreadcrumbs() {
		return (
			<div class={s.breadcrumbs}>
				<button
					class={joinClasses(s.crumb, this.state.currentCard === 'first' && s.active)}
					onClick={() => this.swapToCard('first')}
				/>
				<button
					class={joinClasses(s.crumb, this.state.currentCard === 'second' && s.active)}
					onClick={() => this.swapToCard('second')}
				/>
			</div>
		);
	}

	renderVideo(type, isActive) {
		const { quizResult, resultData } = this.props;

		// selects the winning quiz result, or the first available result if no quiz result
		const quizResultData = quizResult ? resultData[quizResult] : resultData[Object.keys(resultData)[0]];
		const url = quizResultData[`shareableVideo${type}`];
		return (
			<video
				class={joinClasses(s.video, type === 'Feed' && s.feedDimensions, type === 'Stories' && s.storiesDimensions)}
				autoplay={isActive}
				loop
				muted
				playsinline
				preload="auto"
				ref={($ref) => this[`$video${type}`] = $ref}
			>
				<source src={url} type="video/mp4" />
			</video>
		);
	}

	renderStories(order) {
		const isActive = this.state.currentCard === order;
		const { isDesktopDevice, shareable } = this.props;
		const { storiesImageSrc } = this.state;
		const { stories } = shareable;

		return (
			<div class={joinClasses(s.transition, s[order], isActive ? s.active : s.inactive)}>
				<div class={s.cardContainer}>
					{this.props.shareable.shareableVideo ? (
						this.renderVideo('Stories', isActive)
					) : (
						<div class={s.canvasWrapper} onClick={() => this.swapToCard(order)}>
							<canvas class={s.storiesCanvas} ref={($ref) => this.$storiesCanvas = $ref} />
							{!!storiesImageSrc && <img class={s.imageFromCanvas} src={storiesImageSrc} alt={stories.photo?.altText} />}
						</div>
					)}
				</div>
				{isDesktopDevice && (
					<div class={joinClasses(s.cardLabelContainer, this.fontStyles.quizPhotoboothQuestion)}>
						<span class={joinClasses(s.cardLabel, s.show)}>{this.copy.$QUIZPHOTOBOOTH_STORIES}</span>
					</div>
				)}
			</div>
		);
	}

	renderFeed(order) {
		const isActive = this.state.currentCard === order;
		const { isDesktopDevice, shareable } = this.props;
		const { feedImageSrc } = this.state;
		const { feed } = shareable;

		return (
			<div class={joinClasses(s.transition, s[order], isActive ? s.active : s.inactive)}>
				<div class={s.cardContainer}>
					{this.props.shareable.shareableVideo ? (
						this.renderVideo('Feed', isActive)
					) : (
						<div onClick={() => this.swapToCard(order)}>
							<canvas class={s.feedCanvas} ref={($ref) => this.$feedCanvas = $ref} />
							{!!feedImageSrc && <img class={s.imageFromCanvas} src={feedImageSrc} alt={feed.photo?.altText} />}
						</div>
					)}
				</div>
				{isDesktopDevice && (
					<div class={joinClasses(s.cardLabelContainer, this.fontStyles.quizPhotoboothQuestion)}>
						<span class={joinClasses(s.cardLabel, s.show)}>{this.copy.$QUIZPHOTOBOOTH_FEED}</span>
					</div>
				)}
			</div>
		);
	}

	renderSocials() {
		const socials = this.appData?.header?.powsterHeader?.follow;
		if (!socials) return;
		return (
			<div class={s.socials}>
				<HTML class={joinClasses(s.cta, this.fontStyles?.quizPhotoboothMessage)} content={this.copy.$QUIZPHOTOBOOTH_FOLLOW_US} markdown />
				<Socials socials={socials} iconStyle={s.socialIcon} key="socials" />
			</div>
		);
	}

	renderFinalShareable() {
		const { assets, shareable, isDesktopDevice, hasQuiz } = this.props;
		const fontStyles = this.fontStyles;
		const copy = this.copy;
		const { currentCard, frameConfirmed } = this.state;
		const secondCardActive = currentCard === 'second';
		const storiesFirst = shareable?.storiesFirst || false;
		// TODO: ability to have more sizes (e.g. finalShare.js from prime-video-super-college)

		const shouldHide = !frameConfirmed && this.allowChoosingFrame;
		return (
			<div class={joinClasses(s.finalShareableContainer, this.allowChoosingFrame && s.hasChooseFrame, shouldHide && s.hide)}>
				<div
					class={joinClasses(s.carousel, this.props.creativeSpace && s.creativeSpace)}
					onTouchStart={this.onTouchStart}
					onTouchMove={this.onTouchMove}
					onTouchEnd={this.onTouchEnd}
				>
					{storiesFirst ? this.renderStories('first') : this.renderFeed('first')}
					{storiesFirst ? this.renderFeed('second') : this.renderStories('second')}
					<div class={s.nav}>
						<button class={joinClasses(s.prevBtn, !secondCardActive && s.fade)} onClick={() => this.swapToCard('first')}>
							<div class={s.prev} />
						</button>
						<button class={joinClasses(s.nextBtn, secondCardActive && s.fade)} onClick={() => this.swapToCard('second')}>
							<div class={s.next} />
						</button>
					</div>
				</div>
				{this.renderBreadcrumbs()}
				<div class={s.shareContainer}>
					<button class={joinClasses(s.shareBtn, fontStyles?.quizPhotoboothButton)} onClick={this.shareOrDownload}>
						{isDesktopDevice && <span>{copy.$QUIZPHOTOBOOTH_DOWNLOAD}</span>}
						{!isDesktopDevice && (
							<div class={s.shareFeedStory}>
								<span class={joinClasses(s.shareFeed, !secondCardActive && s.show)}>{`${copy.$QUIZPHOTOBOOTH_SHARE} to ${storiesFirst ? copy.$QUIZPHOTOBOOTH_STORIES : copy.$QUIZPHOTOBOOTH_FEED}`}</span>
								<span class={joinClasses(s.shareStory, secondCardActive && s.show)}>{`${copy.$QUIZPHOTOBOOTH_SHARE} to ${storiesFirst ? copy.$QUIZPHOTOBOOTH_FEED : copy.$QUIZPHOTOBOOTH_STORIES}`}</span>
							</div>
						)}
					</button>
				</div>
				<button class={s.takeAgain} onClick={this.restart}>
					<TakeAgain />
					<HTML class={joinClasses(s.cta, s.takeAgainText, fontStyles?.quizPhotoboothQuestion)} content={hasQuiz ? copy.$QUIZPHOTOBOOTH_RETAKE_QUIZ : copy.$QUIZPHOTOBOOTH_RETAKE_PHOTOBOOTH} markdown />
				</button>
				<HTML class={joinClasses(s.cta, fontStyles?.quizPhotoboothMessage)} content={copy.$QUIZPHOTOBOOTH_HASHTAG_CTA} markdown />
				<HTML class={joinClasses(s.cta, s.hashtags, fontStyles?.quizPhotoboothHeader)} content={copy.$QUIZPHOTOBOOTH_HASHTAGS} markdown />
				{shareable.showSocialLinks && this.renderSocials()}
				{assets.bottomImage && <img src={assets.bottomImage} class={s.bottomImage} alt={assets.bottomImageAltText || ''} />}
				{shareable.link && (
					<div class={s.linkContainer} onClick={this.handleLinkClick}>
						<div class={joinClasses(s.link, fontStyles?.quizPhotoboothOption)}>
							<HTML content={shareable.link.copy} markdown />
							<div class={joinClasses(s.next, s.linkArrow)} />
						</div>
					</div>
				)}
			</div>
		);
	}

	renderChooseFrame() {
		const copy = this.props.appData?.copy?.baseCopy || {};
		const { frameConfirmed, frameSelectedIndex, frameCarouselTransform, noFrameCarouselScroll } = this.state;
		const lastFrameSelected = frameSelectedIndex >= this.sortedResultData.length - 1;
		const firstFrameSelected = frameSelectedIndex === 0;

		return (
			<div class={joinClasses(s.chooseFrameContainer, frameConfirmed && s.hide)}>
				<h2 class={s.title}>{copy.$QUIZPHOTOBOOTH_CHOOSE_FRAME}</h2>
				<div class={s.carousel} ref={($ref) => this.$frameCarousel = $ref}>
					<div class={s.frameOptions} ref={($ref) => this.$frameOptions = $ref} style={{ transform: `translateX(${frameCarouselTransform}px)` }}>
						{this.sortedResultData.map((result, index) => (
							<div
								class={joinClasses(s.frameOption, frameSelectedIndex === index && s.selected)}
								onClick={() => noFrameCarouselScroll && this.selectFrame(index)}
							>
								<canvas class={s.chooseFrameCanvas} ref={($ref) => this[`chooseFrame${result.id}`] = $ref} />
							</div>
						))}
					</div>
					<div class={joinClasses(s.nav, noFrameCarouselScroll && s.hide)}>
						<button
							class={joinClasses(s.prevBtn, firstFrameSelected && s.inactive)}
							onClick={!firstFrameSelected && this.onPrevFrameClick}
						>
							<div class={s.prev} />
						</button>
						<button
							class={joinClasses(s.nextBtn, lastFrameSelected && s.inactive)}
							onClick={!lastFrameSelected && this.onNextFrameClick}
						>
							<div class={s.next} />
						</button>
					</div>
				</div>
				<button class={s.confirmFrameBtn} onClick={this.confirmFrameSelection}>{copy.$QUIZPHOTOBOOTH_NEXT}</button>
			</div>
		);
	}

	render() {
		const { shareable, isDesktopWidth, quizResult, resultData } = this.props;
		let shareableBackgroundImage = isDesktopWidth ? shareable.shareableBackgroundDesktop : shareable.shareableBackgroundMobile;

		const quizResultData = quizResult ? resultData[quizResult] : resultData[Object.keys(resultData)[0]];
		if (quizResultData) {
			const backgrounds = shareable?.resultsSpecificBackgrounds || {};
			if (backgrounds && backgrounds[quizResultData?.id]) {
				shareableBackgroundImage = isDesktopWidth ? shareableBackgroundImage = backgrounds[quizResultData?.id]?.shareableBackgroundDesktop : backgrounds[quizResultData?.id]?.shareableBackgroundMobile;
			}
		}

		const isVideo = shareableBackgroundImage?.endsWith('.mp4') || shareableBackgroundImage?.endsWith('.webm');
		const shareableBackgroundStyle = shareableBackgroundImage ? { backgroundImage: `url(${shareableBackgroundImage})` } : {};
		return (
			<div class={joinClasses(s.container, this.allowChoosingFrame && s[`transition-${this.props.transitionStyle}`])} style={shareableBackgroundStyle}>
				{!!shareableBackgroundImage && isVideo && <video class={s.video} src={shareableBackgroundImage} autoplay loop muted playsinline />}
				{this.allowChoosingFrame && this.renderChooseFrame()}
				{this.renderFinalShareable()}
			</div>
		);
	}
}